import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Camera, VideoCamera, Stop, Play, Lightning, FloppyDisk, X, CircleNotch, Eye, CheckCircle, Warning } from '@phosphor-icons/react';

const API = '/api';

function ConfidenceBadge({ score }) {
  const pct = Math.round(score * 100);
  const color = pct >= 80 ? 'bg-green-100 text-green-800' : pct >= 50 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800';
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${color}`}>{pct}%</span>;
}

export default function ARIdentify() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);

  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [sector, setSector] = useState(searchParams.get('sector') || 'aviation');
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [investigations, setInvestigations] = useState([]);
  const [selectedInvestigation, setSelectedInvestigation] = useState(searchParams.get('investigation') || '');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [liveMode, setLiveMode] = useState(false);

  useEffect(() => {
    loadInvestigations();
    return () => stopCamera();
  }, []);

  useEffect(() => {
    if (selectedInvestigation) loadHistory();
  }, [selectedInvestigation]);

  const loadInvestigations = async () => {
    try {
      const { data } = await axios.get(`${API}/investigations`, { withCredentials: true });
      setInvestigations(data);
    } catch (err) {
      console.error('Failed to load investigations for AR:', err);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/ar/history/${selectedInvestigation}`, { withCredentials: true });
      setHistory(data);
    } catch (err) {
      console.error('Failed to load AR history:', err);
    }
  };

  const startCamera = async () => {
    setCameraError('');
    try {
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);
    } catch (err) {
      setCameraError(err.name === 'NotAllowedError'
        ? 'Camera access denied. Please allow camera permissions in your browser settings.'
        : 'Could not access camera. Please ensure a camera is available and try again.');
    }
  };

  const stopCamera = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
    setLiveMode(false);
  };

  const captureFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return null;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    return dataUrl.split(',')[1]; // Return base64 without prefix
  }, []);

  const analyzeFrame = async (base64Data) => {
    if (!base64Data) return;
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/ar/identify`, {
        image_base64: base64Data,
        investigation_id: selectedInvestigation || null,
        sector,
      }, { withCredentials: true });
      setResults(data);
    } catch (err) {
      setResults({
        identified_parts: [],
        scene_description: err.response?.data?.detail || 'Analysis failed. Please try again.',
        damage_assessment: '',
        recommendations: [],
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleCapture = () => {
    const base64 = captureFrame();
    if (base64) {
      setCapturedImage(base64);
      analyzeFrame(base64);
    }
  };

  const toggleLiveMode = () => {
    if (liveMode) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setLiveMode(false);
    } else {
      setLiveMode(true);
      // Analyze every 5 seconds in live mode
      intervalRef.current = setInterval(() => {
        const base64 = captureFrame();
        if (base64 && !analyzing) {
          setCapturedImage(base64);
          analyzeFrame(base64);
        }
      }, 5000);
      // Immediately capture first frame
      const base64 = captureFrame();
      if (base64) {
        setCapturedImage(base64);
        analyzeFrame(base64);
      }
    }
  };

  const handleSaveEvidence = async () => {
    if (!selectedInvestigation || !capturedImage || !results?.identified_parts?.length) return;
    setSaving(true);
    try {
      await axios.post(`${API}/ar/save-evidence`, {
        image_base64: capturedImage,
        investigation_id: selectedInvestigation,
        identified_parts: results.identified_parts,
        confidence_scores: {},
        notes: results.scene_description || '',
      }, { withCredentials: true });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      loadHistory();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error saving evidence');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="ar-identify-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>AR Parts Identification</h1>
          <p className="text-slate-500 mt-1">Use AI-powered vision to identify aircraft and vessel parts from camera feed</p>
        </div>
      </div>

      {/* Linked Investigation Banner */}
      {selectedInvestigation && selectedInvestigation !== 'none' && (() => {
        const linked = investigations.find(i => i.investigation_id === selectedInvestigation || i.investigation_number === selectedInvestigation || i.id === selectedInvestigation);
        return linked ? (
          <div className="bg-gradient-to-r from-cyan-50 to-teal-50 border border-cyan-200 rounded-xl p-4 mb-6 flex items-center justify-between" data-testid="linked-investigation-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                <Camera size={20} className="text-cyan-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-cyan-900">Linked to Investigation: {linked.investigation_number || linked.investigation_id}</p>
                <p className="text-xs text-cyan-700">{linked.title || linked.incident_title || 'Investigation'} &mdash; Identified parts will be saved as evidence</p>
              </div>
            </div>
            <Button variant="outline" size="sm" className="rounded-lg text-cyan-700 border-cyan-300" onClick={() => setSelectedInvestigation('')}>
              <X size={14} className="mr-1" /> Unlink
            </Button>
          </div>
        ) : null;
      })()}

      {/* Controls */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="w-48">
            <label className="text-xs font-medium text-slate-500 uppercase mb-1 block">Sector</label>
            <Select value={sector} onValueChange={setSector}>
              <SelectTrigger className="rounded-lg" data-testid="ar-sector-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="aviation">Aviation</SelectItem>
                <SelectItem value="maritime">Maritime</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-medium text-slate-500 uppercase mb-1 block">Link to Investigation</label>
            <Select value={selectedInvestigation} onValueChange={setSelectedInvestigation}>
              <SelectTrigger className="rounded-lg" data-testid="ar-investigation-select">
                <SelectValue placeholder="Select investigation (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No investigation</SelectItem>
                {investigations.map(inv => (
                  <SelectItem key={inv.id || inv.investigation_id} value={inv.investigation_number || inv.investigation_id || inv.id}>
                    {inv.investigation_number || inv.investigation_id} - {inv.title || inv.incident_title || 'Investigation'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            {!cameraActive ? (
              <Button onClick={startCamera} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg" data-testid="start-camera-btn">
                <Camera size={18} className="mr-2" />
                Start Camera
              </Button>
            ) : (
              <>
                <Button onClick={stopCamera} variant="outline" className="border-red-300 text-red-600 rounded-lg" data-testid="stop-camera-btn">
                  <Stop size={18} className="mr-2" />
                  Stop
                </Button>
                <Button onClick={handleCapture} disabled={analyzing} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="capture-btn">
                  {analyzing ? <CircleNotch size={18} className="mr-2 animate-spin" /> : <Camera size={18} className="mr-2" />}
                  Capture & Identify
                </Button>
                <Button
                  onClick={toggleLiveMode}
                  className={`rounded-lg ${liveMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-purple-600 hover:bg-purple-700'} text-white`}
                  data-testid="live-mode-btn"
                >
                  {liveMode ? <Stop size={18} className="mr-2" /> : <Lightning size={18} className="mr-2" />}
                  {liveMode ? 'Stop Live' : 'Live Mode'}
                </Button>
              </>
            )}
            {selectedInvestigation && selectedInvestigation !== 'none' && (
              <Button variant="outline" onClick={() => setShowHistory(true)} className="rounded-lg" data-testid="history-btn">
                <Eye size={18} className="mr-2" />
                History ({history.length})
              </Button>
            )}
          </div>
        </div>
      </div>

      {cameraError && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-6" data-testid="camera-error">
          <div className="flex items-center gap-2">
            <Warning size={20} weight="fill" />
            <span className="font-medium">{cameraError}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Camera Feed */}
        <div className="bg-slate-900 rounded-xl overflow-hidden relative" data-testid="camera-container">
          <div className="aspect-video relative">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={`w-full h-full object-cover ${cameraActive ? '' : 'hidden'}`}
              data-testid="camera-video"
            />
            {!cameraActive && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                <VideoCamera size={64} weight="thin" />
                <p className="mt-4 text-lg font-medium">Camera not active</p>
                <p className="text-sm text-slate-500 mt-1">Click "Start Camera" to begin</p>
              </div>
            )}
            {liveMode && (
              <div className="absolute top-3 left-3 flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm font-bold animate-pulse">
                <div className="w-2 h-2 bg-white rounded-full" />
                LIVE
              </div>
            )}
            {analyzing && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <div className="bg-white/90 backdrop-blur px-6 py-4 rounded-xl flex items-center gap-3">
                  <CircleNotch size={24} className="animate-spin text-blue-600" />
                  <span className="font-medium text-slate-700">Analyzing...</span>
                </div>
              </div>
            )}
          </div>
          <canvas ref={canvasRef} className="hidden" />
        </div>

        {/* Results Panel */}
        <div className="space-y-4">
          {results ? (
            <>
              {/* Scene Description */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="results-panel">
                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-3">Scene Analysis</h3>
                <p className="text-sm text-slate-700 leading-relaxed">{results.scene_description}</p>
                {results.damage_assessment && (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-amber-800 uppercase mb-1">Damage Assessment</p>
                    <p className="text-sm text-amber-700">{results.damage_assessment}</p>
                  </div>
                )}
              </div>

              {/* Identified Parts */}
              {results.identified_parts?.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="identified-parts">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">
                      Identified Parts ({results.identified_parts.length})
                    </h3>
                    {selectedInvestigation && selectedInvestigation !== 'none' && (
                      <Button
                        size="sm"
                        onClick={handleSaveEvidence}
                        disabled={saving || saveSuccess}
                        className={`rounded-lg ${saveSuccess ? 'bg-green-600' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                        data-testid="save-evidence-btn"
                      >
                        {saveSuccess ? (
                          <><CheckCircle size={16} className="mr-1" /> Saved</>
                        ) : saving ? (
                          <><CircleNotch size={16} className="mr-1 animate-spin" /> Saving...</>
                        ) : (
                          <><FloppyDisk size={16} className="mr-1" /> Save as Evidence</>
                        )}
                      </Button>
                    )}
                  </div>
                  <div className="space-y-3">
                    {results.identified_parts.map((part, idx) => (
                      <div key={`${part.name}-${idx}`} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-slate-900">{part.name}</span>
                              <ConfidenceBadge score={part.confidence} />
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">{part.category}</span>
                              {part.condition && (
                                <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                                  part.condition?.includes('intact') ? 'bg-green-100 text-green-700' :
                                  part.condition?.includes('damaged') ? 'bg-red-100 text-red-700' :
                                  'bg-amber-100 text-amber-700'
                                }`}>{part.condition}</span>
                              )}
                            </div>
                          </div>
                          {part.location_in_image && (
                            <span className="text-[10px] text-slate-400">{part.location_in_image}</span>
                          )}
                        </div>
                        {part.notes && <p className="text-xs text-slate-500 mt-1.5">{part.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {results.recommendations?.length > 0 && (
                <div className="bg-blue-50 rounded-xl border border-blue-200 p-5">
                  <h3 className="text-sm font-semibold text-blue-900 uppercase tracking-wider mb-2">Recommendations</h3>
                  <ul className="space-y-1">
                    {results.recommendations.map((rec, idx) => (
                      <li key={`rec-${idx}`} className="text-sm text-blue-800 flex items-start gap-2">
                        <span className="text-blue-400 mt-0.5">-</span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Lightning size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">No analysis yet</p>
              <p className="text-sm text-slate-400 mt-1">Start the camera and capture a frame to identify parts</p>
            </div>
          )}
        </div>
      </div>

      {/* Captured Image Preview */}
      {capturedImage && (
        <div className="mt-6 bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-3">Last Captured Frame</h3>
          <img
            src={`data:image/jpeg;base64,${capturedImage}`}
            alt="Captured frame"
            className="rounded-lg max-h-[200px] object-contain"
            data-testid="captured-image"
          />
        </div>
      )}

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>AR Identification History</DialogTitle>
          </DialogHeader>
          {history.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No identification history for this investigation</p>
          ) : (
            <div className="space-y-4">
              {history.map((h) => (
                <div key={h.ar_id} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-900">{h.identified_parts?.length || 0} parts identified</span>
                    <span className="text-xs text-slate-500">{new Date(h.identified_at).toLocaleString()}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(h.identified_parts || []).map((p, i) => (
                      <span key={`${p.name}-${i}`} className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{p.name}</span>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">By: {h.identified_by}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
